import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DOMPurify from 'dompurify';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useAccount } from '../../contexts/AccountContext';
import { usePricing } from '../../contexts/PricingContext';
import { useTeamLimits } from '../../hooks/useTeamLimits';
import { applyBatchFixes, type ImpactItem, sectionChat } from '../../services/tenderAIService';
import { logAIActivity } from '../../utils/activityLogger';
import { EstimatedCostChip } from '../common/EstimatedCostChip';

interface Message {
  id?: string;
  role: 'user' | 'model';
  content: string;
  type?: 'text' | 'suggestion' | 'impact_alert';
  suggestionContent?: string;
  impacts?: ImpactItem[];
}

interface SectionInfo {
  id: string;
  title: string;
}

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RemoveIcon from '@mui/icons-material/Remove';
import UndoIcon from '@mui/icons-material/Undo';

const EMPTY_ARRAY: never[] = [];

interface SectionChatProps {
  tenderId: string;
  sectionId: string;
  currentContent: string;
  onClose: () => void;
  onMinimize?: () => void;
  isMinimized?: boolean;
  onApplyEdit?: (newContent: string) => void;
  onReceiveSuggestion?: (suggestion: string) => void;
  allSections?: SectionInfo[];
  onSectionChange?: (sectionId: string) => void;
  onNavigateToSection?: (sectionId: string) => void;
  canUndo?: boolean;
  onUndo?: () => void;
  isNewSection?: boolean;
  usedCount?: number;
  canEditAllSections?: boolean;
}

const SectionChat: React.FC<SectionChatProps> = ({
  tenderId,
  sectionId,
  currentContent,
  onClose,
  onMinimize,
  isMinimized,
  onApplyEdit,
  onReceiveSuggestion,
  allSections = [],
  onSectionChange,
  onNavigateToSection,
  canUndo,
  onUndo,
  isNewSection = false,
  usedCount = 0,
  canEditAllSections = false,
}) => {
  const theme = useTheme();
  const { account } = useAccount();
  const { pricingSettings } = usePricing();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [fixing, setFixing] = useState<boolean>(false);
  const [fixed, setFixed] = useState<Set<string>>(new Set());
  const [queuedFixes, setQueuedFixes] = useState<Map<string, ImpactItem>>(new Map());
  const prevSectionIdRef = useRef(sectionId);
  const inputRef = useRef<HTMLInputElement>(null);

  const { checkLimit } = useTeamLimits();
  // Pass the actual sectionChatCount from the tender
  const { remaining, isLimitReached } = checkLimit('sectionChats', usedCount);
  const remainingChats = remaining === 'Unlimited' ? null : remaining;

  // Auto-focus input when chat is ready
  useEffect(() => {
    if (!isMinimized && !loading && !initialLoading) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isMinimized, loading, initialLoading]);

  // Reset history when sectionId changes
  useEffect(() => {
    if (prevSectionIdRef.current !== sectionId) {
      setMessages([]);
      setInitialLoading(true);
      setTimeout(() => {
        const initialMsg = isNewSection
          ? "This section is empty. Tell me what you would like to write here (e.g. 'Write a methodology for risk management'), and I will draft it for you based on the tender documents."
          : 'How can I help? I can provide information from the tender docs or suggest edits for this section.';

        setMessages([
          {
            role: 'model',
            content: initialMsg,
            type: 'text',
          },
        ]);
        setInitialLoading(false);
      }, 1000);
      prevSectionIdRef.current = sectionId;
    } else if (messages.length === 0 && !initialLoading) {
      const initialMsg = isNewSection
        ? "This section is empty. Tell me what you would like to write here (e.g. 'Write a methodology for risk management'), and I will draft it for you based on the tender documents."
        : 'How can I help? I can provide information from the tender docs or suggest edits for this section.';

      setMessages([
        {
          role: 'model',
          content: initialMsg,
          type: 'text',
        },
      ]);
    }
  }, [sectionId, messages.length, initialLoading, isNewSection]);

  const parseResponse = (text: string): Message => {
    const suggestionMatch = text.match(/<suggestion>([\s\S]*?)<\/suggestion>/);
    if (suggestionMatch) {
      let suggestion = suggestionMatch[1] ? suggestionMatch[1].trim() : '';
      // Clean markdown code blocks if present
      suggestion = suggestion.replace(/^\s*```\w*\s*[\r\n]*/, '').replace(/[\r\n]*```\s*$/, '');

      if (onReceiveSuggestion && suggestion) {
        onReceiveSuggestion(suggestion);
        return {
          id: crypto.randomUUID(),
          role: 'model',
          content:
            'Revisions complete! Please review the changes then Accept or Reject them. If you would like me to try again, please provide new instructions.',
          type: 'text',
        };
      }
      return {
        id: crypto.randomUUID(),
        role: 'model',
        content: text.replace(suggestionMatch[0], '').trim() || 'Here is a suggested edit:',
        type: 'suggestion',
        suggestionContent: suggestion,
      };
    }
    return { id: crypto.randomUUID(), role: 'model', content: text, type: 'text' };
  };

  const handleApprove = (msg: Message) => {
    if (msg.suggestionContent && onApplyEdit) {
      onApplyEdit(msg.suggestionContent);
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: '✅ Suggestion Approved.', type: 'text' },
      ]);
    }
  };

  const handleReject = () => {
    setInput('I rejected this suggestion. Please ');
  };

  const handleToggleQueue = (impact: ImpactItem) => {
    const key = JSON.stringify(impact);
    setQueuedFixes((prev) => {
      const next = new Map(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, impact);
      }
      return next;
    });
  };

  const handleApplyBatchFixes = async () => {
    if (!account?.clientId || !account?.uid || queuedFixes.size === 0) {
      return;
    }

    setFixing(true);
    const fixesToApply = Array.from(queuedFixes.values());

    try {
      const result = await applyBatchFixes({
        tenderId,
        fixes: fixesToApply,
        clientId: account.clientId,
        userId: account.uid,
        teamId: account.teamId,
      });

      if (result.success) {
        setFixed((prev) => {
          const next = new Set(prev);
          for (const f of fixesToApply) {
            next.add(JSON.stringify(f));
          }
          return next;
        });
        setQueuedFixes(new Map());

        const appliedMessage = `✅ <strong>Auto-Fixes Applied:</strong> ${fixesToApply.length} sections have been updated with suggestions. Please review them.`;
        setMessages((prev) => [...prev, { role: 'model', content: appliedMessage, type: 'text' }]);

        if (result.impact_report && result.impact_report.length > 0) {
          setMessages((prev) => [
            ...prev,
            {
              id: crypto.randomUUID(),
              role: 'model',
              content:
                '⚠️ <strong>New Impacts Detected:</strong> The recent fixes introduced new cross-section inconsistencies.',
              type: 'impact_alert',
              impacts: result.impact_report as ImpactItem[],
            },
          ]);
        }
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: 'model',
            content: `❌ <strong>Error:</strong> Failed to apply batch fixes.`,
            type: 'text',
          },
        ]);
      }
    } catch (_error) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'model',
          content: '❌ <strong>Error:</strong> Could not connect to apply fixes.',
          type: 'text',
        },
      ]);
    } finally {
      setFixing(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || !account?.clientId) {
      return;
    }

    const newMsg: Message = { role: 'user', content: input, type: 'text' };
    setMessages((prev) => [...prev, newMsg]);
    setInput('');
    setLoading(true);

    // Optimistic usage update

    try {
      const response = await sectionChat({
        tenderId,
        sectionId,
        message: input,
        currentContent,
        history: messages.map((m) => ({ role: m.role, content: m.content })),
        clientId: account.clientId,
        teamId: account.teamId,
      });

      if (response.success && response.response) {
        const parsed = parseResponse(response.response);
        setMessages((prev) => [...prev, parsed]);

        // Check for Cross-Section Impacts
        if (response.impact_report && response.impact_report.length > 0) {
          if (!canEditAllSections) {
            // Automatically submit to owner behind the scenes
            applyBatchFixes({
              tenderId,
              fixes: response.impact_report,
              clientId: account.clientId,
              userId: account.uid,
              teamId: account.teamId,
            }).catch(() => {
              // Fire and forget
            });

            setMessages((prev) => [
              ...prev,
              {
                role: 'model',
                content:
                  '⚠️ <strong>Heads Up:</strong> Your changes triggered AI consistency fixes for other sections. These have been automatically forwarded to the Tender Owner for approval.',
                type: 'text',
              },
            ]);
          } else {
            // Show interactive queue for authorized users
            setMessages((prev) => [
              ...prev,
              {
                role: 'model',
                content: '⚠️ <strong>Heads Up:</strong> This change might affect other sections.',
                type: 'impact_alert',
                impacts: response.impact_report || EMPTY_ARRAY,
              },
            ]);
          }
        }
        logAIActivity.serviceUsed({
          serviceName: 'Section Chat',
          serviceType: 'chat_interaction',
          inputType: 'text',
          success: true,
        });
      } else {
        setMessages((prev) => [
          ...prev,
          { role: 'model', content: 'Error: Could not get response.', type: 'text' },
        ]);
      }
    } catch (_e) {
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: 'model',
          content: 'Error: Could not connect to AI.',
          type: 'text',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        onClick={onMinimize}
        sx={{
          p: 1,
          px: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          bgcolor: theme.palette.bidlogiq?.brick || '#A52A2A',
          color: 'white',
          gap: 1,
          minHeight: '50px', // Ensure visible when minimized
          cursor: 'pointer',
        }}
      >
        <Box
          sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}
        >
          {!isMinimized && (
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.8)', whiteSpace: 'nowrap' }}
            >
              Working in:
            </Typography>
          )}
          {onSectionChange && allSections.length > 0 && !isMinimized ? (
            <FormControl
              size="small"
              fullWidth
              variant="standard"
              onClick={(e) => e.stopPropagation()}
              sx={{ maxWidth: '65%' }}
            >
              <Select
                value={sectionId}
                onChange={(e) => onSectionChange(e.target.value)}
                disableUnderline
                sx={{
                  fontWeight: 'bold',
                  fontSize: '0.85rem',
                  color: 'text.primary',
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  px: 1,
                  py: 0.2,
                  '.MuiSelect-icon': { color: 'text.primary' },
                  '& .MuiSelect-select': { py: 0, pr: '24px !important' },
                  boxShadow: 1,
                }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
              >
                {allSections.map((sec) => (
                  <MenuItem key={sec.id} value={sec.id}>
                    {sec.title.length > 25 ? `${sec.title.substring(0, 25)}...` : sec.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography variant="subtitle2" fontWeight="bold">
              AI Assistant
            </Typography>
          )}
        </Box>
        {/* Remaining Chats Badge */}
        {!isMinimized && remainingChats !== null && (
          <Chip
            label={`${remainingChats} left`}
            size="small"
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              height: 24,
              fontSize: '0.75rem',
              fontWeight: 'bold',
              mr: 1,
            }}
          />
        )}
        {canUndo && onUndo && !isMinimized && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onUndo();
            }}
            sx={{ color: 'white' }}
            title="Undo last change"
          >
            <UndoIcon fontSize="small" />
          </IconButton>
        )}
        {onMinimize && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onMinimize();
            }}
            sx={{ color: 'white' }}
          >
            {isMinimized ? <ExpandLessIcon fontSize="small" /> : <RemoveIcon fontSize="small" />}
          </IconButton>
        )}
        <IconButton
          size="small"
          aria-label="Close AI Chat"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          sx={{ color: 'white' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {!isMinimized && (
        <>
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {initialLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  flexDirection: 'column',
                  gap: 2,
                  color: 'text.secondary',
                }}
              >
                <CircularProgress size={30} />
                <Typography variant="body2">Connecting to section context...</Typography>
              </Box>
            ) : (
              <>
                {messages.map((m, i) => {
                  const isUser = m.role === 'user';
                  const isDarkMode = theme.palette.mode === 'dark';

                  let bgColor = '';
                  let textColor = '';

                  if (isUser) {
                    bgColor = isDarkMode
                      ? theme.palette.grey[800]
                      : theme.palette.bidlogiq?.taupe || '#D2B48C';
                    textColor = isDarkMode ? theme.palette.common.white : 'black';
                  } else {
                    bgColor = isDarkMode
                      ? 'rgba(208, 92, 9, 0.2)'
                      : theme.palette.bidlogiq?.brick || '#A52A2A';
                    textColor = isDarkMode ? theme.palette.common.white : 'white';
                  }

                  return (
                    <Box
                      key={m.id || i}
                      sx={{
                        alignSelf: isUser ? 'flex-end' : 'flex-start',
                        bgcolor: bgColor,
                        color: textColor,
                        p: 1.5,
                        borderRadius: 2,
                        maxWidth: '90%',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        border:
                          m.type === 'suggestion'
                            ? `1px solid ${theme.palette.secondary.main}`
                            : 'none',
                        '& p': { margin: 0 },
                        '& ul, & ol': { pl: 2, my: 0.5 },
                        '& li': { mb: 0.5 },
                      }}
                    >
                      {/* biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitized */}
                      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(m.content) }} />

                      {m.type === 'suggestion' && m.suggestionContent && (
                        <Box
                          sx={{
                            mt: 1,
                            p: 1,
                            bgcolor:
                              theme.palette.mode === 'dark'
                                ? 'rgba(0, 0, 0, 0.4)'
                                : 'rgba(255, 255, 255, 0.9)',
                            borderRadius: 1,
                            border: `1px dashed ${theme.palette.divider}`,
                            color: theme.palette.mode === 'dark' ? 'text.primary' : 'text.primary',
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            display="block"
                            mb={0.5}
                          >
                            Suggested Edit:
                          </Typography>
                          <div
                            // biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitized
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(m.suggestionContent),
                            }}
                            style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '0.9em' }}
                          />
                          <Divider sx={{ my: 1 }} />
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<CancelIcon />}
                              onClick={handleReject}
                            >
                              Reject
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => handleApprove(m)}
                            >
                              Approve
                            </Button>
                          </Box>
                        </Box>
                      )}

                      {m.type === 'impact_alert' && m.impacts && (
                        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {m.impacts.map((impact, idx) => {
                            const affectedSection = allSections.find(
                              (s) => s.id === impact.section_id
                            );
                            return (
                              <Box
                                key={impact.section_id || `impact-${idx}`}
                                sx={{
                                  p: 1.5,
                                  bgcolor:
                                    theme.palette.mode === 'dark'
                                      ? 'rgba(0, 0, 0, 0.4)'
                                      : 'rgba(255, 255, 255, 0.9)',
                                  borderRadius: 1,
                                  color:
                                    theme.palette.mode === 'dark' ? 'text.primary' : 'text.primary',
                                  border: '1px solid',
                                  borderColor: 'error.main',
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 1,
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <WarningIcon
                                      fontSize="small"
                                      color={impact.urgency === 'high' ? 'error' : 'warning'}
                                    />
                                    <Typography variant="subtitle2" fontWeight="bold">
                                      {affectedSection
                                        ? affectedSection.title
                                        : impact.section_title ||
                                          impact.section_id ||
                                          'Related Section'}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    label={impact.urgency.toUpperCase()}
                                    size="small"
                                    color={impact.urgency === 'high' ? 'error' : 'warning'}
                                    sx={{ height: 20, fontSize: '0.65rem' }}
                                  />
                                </Box>
                                <Box sx={{ mb: 1.5, '& p': { m: 0, fontSize: '0.875rem' } }}>
                                  <ReactMarkdown>
                                    {typeof impact.instruction === 'string'
                                      ? impact.instruction
                                      : (impact.instruction.instruction as string) ||
                                        `Replace "${impact.instruction.old_text as string}" with "${impact.instruction.new_text as string}"`}
                                  </ReactMarkdown>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color={
                                      fixed.has(JSON.stringify(impact)) ? 'success' : undefined
                                    }
                                    startIcon={
                                      fixed.has(JSON.stringify(impact)) ? (
                                        <CheckCircleIcon />
                                      ) : queuedFixes.has(JSON.stringify(impact)) ? (
                                        <CancelIcon />
                                      ) : (
                                        <AutoFixHighIcon />
                                      )
                                    }
                                    onClick={() => handleToggleQueue(impact)}
                                    disabled={fixing || fixed.has(JSON.stringify(impact))}
                                    sx={{
                                      flexGrow: 1,
                                      textTransform: 'none',
                                      ...(!fixed.has(JSON.stringify(impact)) && {
                                        bgcolor: theme.palette.bidlogiq?.brick || '#A52A2A',
                                        color: 'white',
                                        '&:hover': {
                                          bgcolor: theme.palette.bidlogiq?.brick || '#A52A2A',
                                          filter: 'brightness(0.9)',
                                        },
                                      }),
                                    }}
                                  >
                                    {fixed.has(JSON.stringify(impact))
                                      ? 'Fixed!'
                                      : queuedFixes.has(JSON.stringify(impact))
                                        ? 'Remove from Queue'
                                        : 'Queue Fix'}
                                  </Button>

                                  {affectedSection && (onNavigateToSection || onSectionChange) && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      endIcon={<ArrowForwardIcon />}
                                      onClick={() =>
                                        onNavigateToSection
                                          ? onNavigateToSection(impact.section_id)
                                          : onSectionChange?.(impact.section_id)
                                      }
                                      sx={{ textTransform: 'none' }}
                                    >
                                      Go to Section
                                    </Button>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  );
                })}
                {loading && <CircularProgress size={20} />}
              </>
            )}
          </Box>

          {queuedFixes.size > 0 && (
            <Box
              sx={{
                p: 1.5,
                borderTop: 1,
                borderColor: 'divider',
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.04)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="body2" fontWeight="bold">
                {queuedFixes.size} Fix{queuedFixes.size !== 1 ? 'es' : ''} Queued
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyBatchFixes}
                disabled={fixing}
                startIcon={
                  fixing ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />
                }
                sx={{
                  textTransform: 'none',
                  bgcolor: theme.palette.bidlogiq?.brick || '#A52A2A',
                  color: 'white',
                  '&:hover': {
                    bgcolor: theme.palette.bidlogiq?.brick || '#A52A2A',
                    filter: 'brightness(0.9)',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'action.disabledBackground',
                    color: 'action.disabled',
                  },
                }}
              >
                {fixing ? 'Applying...' : 'Apply All Fixes'}
              </Button>
            </Box>
          )}

          <Box
            sx={{
              p: 1,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                multiline
                maxRows={6}
                size="small"
                placeholder={
                  isLimitReached
                    ? 'Section chat limit reached'
                    : 'Ask a question, or request changes...'
                }
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                disabled={loading || initialLoading || isLimitReached}
                inputRef={inputRef}
                sx={{
                  bgcolor:
                    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'greyLight',
                  '& .MuiInputBase-input::placeholder': {
                    color: 'text.secondary',
                    opacity: 1,
                  },
                }}
                slotProps={{
                  htmlInput: { maxLength: 500 },
                }}
              />
              <IconButton
                onClick={handleSend}
                disabled={loading || initialLoading || !input.trim() || isLimitReached}
                aria-label="send message"
                sx={{
                  bgcolor: theme.palette.bidlogiq?.brick || '#A52A2A',
                  color: 'white',
                  '&:hover': {
                    bgcolor: theme.palette.bidlogiq?.brick || '#A52A2A',
                    filter: 'brightness(0.9)',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'action.disabledBackground',
                    color: 'action.disabled',
                  },
                }}
              >
                <SendIcon />
              </IconButton>
              <Tooltip
                title="Exact billing depends on the length and complexity of the tender documents and provided context."
                slotProps={{ tooltip: { sx: { fontSize: '0.875rem', p: 1 } } }}
              >
                <EstimatedCostChip
                  estimate={pricingSettings?.estimates?.section_revision || 'Est. $1.00 – $5.00'}
                  sx={{ fontSize: '0.65rem' }}
                />
              </Tooltip>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default SectionChat;
